import { useState, useEffect, useRef } from "react";
import { Search, Loader2, User, Phone, Paperclip, ArrowRightCircle, Edit } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/ui/components/ui/dialog";
import { Input } from "@/ui/components/ui/input";
import { useActions, useAppState } from "@/state/legacy_module_api";
import { Message, ContentType } from "@/data/schemas";
import { cn } from "@/utils/module_api";
import { formatDateTime, MessageType } from "@/core_bridge/module_api";

interface ThreadSearchPopupProps {
  opened: boolean;
  onPopupClose: () => void;
  receiverId?: string;
  senderId?: string;
  groupId?: string;
}

const ThreadSearchPopup: React.FC<ThreadSearchPopupProps> = ({
  opened,
  onPopupClose,
  receiverId,
  senderId,
  groupId,
}) => {
  const { searchMessages, clearSearch } = useActions().thread;
  const { search } = useAppState().thread;
  const { contactsById } = useAppState().user;
  const myUserId = useAppState().myUserId;

  const [localQuery, setLocalQuery] = useState("");
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setLocalQuery(query);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      searchMessages({ query });
    }, 300);
  };

  useEffect(() => {
    if (!opened) {
      clearSearch();
      setLocalQuery("");
    }
  }, [opened, clearSearch]);

  // Use switch-core formatDateTime utility
  const formatMessageDate = (timestamp: number) => formatDateTime(timestamp);

  const highlightText = (text: string, query: string) => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <Dialog open={opened} onOpenChange={(open) => !open && onPopupClose()}>
      <DialogContent data-name="search-popup" className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Search Messages</DialogTitle>
        </DialogHeader>

        <div data-name="search-input-container" className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-name="search-input"
            value={localQuery}
            onChange={handleSearchInput}
            placeholder="Search messages"
            className="pl-10"
          />
        </div>

        <div data-name="search-results-area" className="flex-1 overflow-auto space-y-4">
          {search.isSearching && (
            <div data-name="search-loading" className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Searching...</span>
            </div>
          )}

          {search.error && (
            <div data-name="search-error" className="text-center text-destructive py-4">
              Error: {search.error}
            </div>
          )}

          {search.query && !search.isSearching && !search.error && (
            <p data-name="search-result-count" className="text-sm text-muted-foreground">
              {search.results.length > 0
                ? `Found ${search.results.length} result${search.results.length === 1 ? "" : "s"}`
                : "No messages found"}
            </p>
          )}

          <div data-name="search-results-list" className="space-y-3">
            {search.results.map((message: Message, index: number) => {
              const senderName =
                contactsById[message.senderId]?.username || "Unknown User";
              const isMyMessage = message.senderId === myUserId;
              const isGif = message.contentType === ContentType.GIPHY;
              const isAttachment = !!message.attachment;
              const isDelivered = message.status === "DELIVERED";
              const isDeleted = !!message.deletedAt;

              return (
                <div
                  key={message.id || index}
                  data-name="search-result-item"
                  data-message-id={message.id}
                  className={cn(
                    "max-w-[85%] rounded-lg px-4 py-2",
                    isMyMessage
                      ? "ml-auto bg-primary text-primary-foreground"
                      : "bg-muted"
                  )}
                >
                  <p className="text-xs opacity-70 mb-1">
                    {isMyMessage ? "Me" : senderName} -{" "}
                    {formatMessageDate(message.createdAt)}
                  </p>

                  {message.type === MessageType.CALL && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-5 h-5" />
                      <div>
                        <div className="font-medium">Join Call</div>
                        <div className="text-xs opacity-70">
                          Room ID: {message.roomId}
                        </div>
                      </div>
                    </div>
                  )}

                  {message.type === MessageType.CALL_JOINED && (
                    <div>
                      <span>📞 Joined call in room </span>
                      <strong>{message.roomId}</strong>
                    </div>
                  )}

                  {isGif && (
                    <img
                      src={message.content}
                      alt="GIF"
                      className="max-w-60 rounded-lg"
                    />
                  )}

                  {message.type === MessageType.MESSAGE &&
                    !isGif &&
                    !message.encryptedContent && (
                      <div>{highlightText(message.content || "", search.query)}</div>
                    )}

                  {message.type === MessageType.COMMUNITY_UPDATED && (
                    <small className="flex items-center gap-1">
                      <Edit className="w-4 h-4" />
                      {senderName} added you to community.
                    </small>
                  )}

                  {message.type === MessageType.THREAD_CREATED && (
                    <small>✅ Created this group.</small>
                  )}

                  {message.type === MessageType.EXIT_THREAD && (
                    <div className="flex items-center gap-2">
                      <ArrowRightCircle className="w-5 h-5" />
                      <span>{senderName} has left the group.</span>
                    </div>
                  )}

                  {!isGif &&
                    typeof message.content !== "string" &&
                    message.type === MessageType.MESSAGE &&
                    message.encryptedContent && (
                      <small>🚫 This message cannot be decrypted.</small>
                    )}

                  {isAttachment && (
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-5 h-5" />
                      <span>{message.attachment?.mimeType}</span>
                    </div>
                  )}

                  {isDeleted && <small>🚫 You deleted this message.</small>}

                  {isDelivered && !isDeleted && <span className="text-xs">✓</span>}
                </div>
              );
            })}
          </div>

          {!search.query && (
            <div data-name="search-empty-state" className="text-center py-12">
              <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Enter text to search through your thread history
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ThreadSearchPopup;
